import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { BookOpen, FileText, Calculator, Thermometer, Sun, ClipboardList, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';

const Resources = () => {
  const guides = [
    {
      title: "What is an EPC? Complete Guide",
      description: "Everything you need to know about Energy Performance Certificates - what they are, why you need one, how ratings work, and how to improve your score.",
      link: "/resources/epc-explained",
      icon: FileText,
      category: "EPC Basics"
    },
    {
      title: "SAP vs EPC Explained",
      description: "Understanding the difference between SAP calculations and EPCs. When you need each one, how they relate, and what they mean for your project.",
      link: "/resources/sap-vs-epc",
      icon: Calculator,
      category: "Technical"
    },
    {
      title: "U-Values Explained with Examples",
      description: "A practical guide to U-values - what they measure, Building Regulations requirements, and real-world examples for walls, floors, roofs and windows.",
      link: "/resources/u-value-examples",
      icon: Thermometer,
      category: "Technical"
    },
    {
      title: "Psi Values & Thermal Bridging Guide",
      description: "Understanding thermal bridges at building junctions. What Psi values mean, why they matter, and how calculated values can improve your SAP rating.",
      link: "/resources/psi-values-explained",
      icon: Calculator,
      category: "Technical"
    },
    {
      title: "Part O Overheating Guide",
      description: "Complete guide to Part O Building Regulations for overheating in new homes. Compliance methods, risk factors, and mitigation strategies.",
      link: "/resources/part-o-overheating-guide",
      icon: Sun,
      category: "Building Regulations"
    },
    {
      title: "Lease Plan Requirements Guide",
      description: "What you need to know about lease plans for Land Registry. Requirements, compliance, common issues, and how to ensure your lease plan is accepted.",
      link: "/resources/lease-plan-requirements",
      icon: ClipboardList,
      category: "Property"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Resources & Guides | EPC, SAP, Building Regulations | Energy Performance Direct</title>
        <meta name="description" content="Free guides and resources on EPCs, SAP calculations, U-values, thermal bridging, Part O overheating, and lease plans. Expert advice from Energy Performance Direct." />
        <meta name="keywords" content="EPC guide, SAP explained, U-value guide, thermal bridging guide, Part O guide, lease plan guide, energy assessment resources" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/resources" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-green-50 via-emerald-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Free Expert Guides</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Resources & Guides
              </h1>
              <p className="text-xl text-gray-600">
                Expert guides on energy assessments, Building Regulations compliance, and property documentation. Written by qualified assessors to help you understand what you need.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {guides.map((guide, index) => (
                  <Link key={index} to={guide.link} className="block group">
                    <Card className="h-full hover:shadow-lg transition-all hover:border-green-300">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">{guide.category}</span>
                        </div>
                        <guide.icon className="h-10 w-10 text-green-600 mb-4" />
                        <h2 className="text-xl font-semibold mb-3 group-hover:text-green-600 transition-colors">{guide.title}</h2>
                        <p className="text-gray-600 mb-4">{guide.description}</p>
                        <div className="flex items-center text-green-600 font-medium">
                          Read Guide <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <BookOpen className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">Need More Help?</h2>
              <p className="text-gray-600 mb-6">
                Our guides cover the essentials, but every property is different. If you have specific questions about your project, our team is here to help.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                  Contact Us
                </Link>
                <Link to="/faq" className="inline-flex items-center px-6 py-3 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors">
                  View FAQs
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Resources;
